/**
 * Contextual agent descriptors
 *
 * Reusable AgentDescriptor factories for the agents that surface in the
 * AgentBar across multiple pages (Scoreboard, Decisions, Session Results,
 * Relationships). Each page imports the factories it needs, decides which are
 * relevant for its context, and renders them with <AgentBar>.
 *
 * Unlike ATC/Nexus on the item detail page — which run inline and write their
 * result back onto the page — these agents own a dedicated report/home surface
 * where their run controls and findings already live. Here the pill is a
 * contextual launcher: it explains what the agent does, then takes the user to
 * that surface. This keeps the "an agent is relevant here" signal ambient and
 * non-disruptive without duplicating each agent's full experience.
 */

import type { AgentDescriptor } from './types';

/** Minimal navigate signature — matches react-router's NavigateFunction for a string path. */
type NavigateTo = (to: string) => void;

/**
 * Twins — synthetic stakeholder modelling.
 * Relevant wherever a ranking is being read or produced (Scoreboard, Session
 * Results): it predicts how stakeholders would score the items in play.
 */
export function twinsAgent(navigate: NavigateTo): AgentDescriptor {
  return {
    id: 'twins',
    name: 'Twins',
    tagline: 'Predict how stakeholders would score these priorities',
    description:
      'LLM Twins build synthetic stakeholder personas from how participants have scored before, then predict how each would rate the items in play — surfacing likely disagreement before you bring people into a room. Predictions are advisory and never replace real participant input.',
    capabilities: [
      { label: 'Model stakeholder perspectives', description: 'Builds twins from prior participation patterns.' },
      { label: 'Predict likely scores', description: 'Estimates how each twin would rank or rate the current items.' },
      { label: 'Flag probable disagreement', description: 'Highlights where perspectives are likely to diverge.' },
    ],
    glyphType: 'twins',
    colorScheme: 'indigo',
    runLabel: 'Open Twins report',
    onRun: () => { navigate('/prioritization/agents/twins'); },
  };
}

/**
 * Stakeholder Pulse — post-publish feedback loop.
 * Relevant where decisions/outcomes are recorded (Decisions): it closes the
 * loop with the people who submitted ideas once a list is published.
 */
export function stakeholderPulseAgent(navigate: NavigateTo): AgentDescriptor {
  return {
    id: 'stakeholder-pulse',
    name: 'Stakeholder Pulse',
    tagline: 'Close the loop with idea submitters after publishing',
    description:
      'Stakeholder Pulse notifies the people who submitted ideas once a priority list is published, tells them what happened to their idea, and collects whether they feel the outcome addressed their concern — turning a one-way decision into a measurable feedback signal.',
    capabilities: [
      { label: 'Notify submitters of outcomes', description: 'Sends each contributor what happened to the idea they raised.' },
      { label: 'Collect an addressed / not-addressed signal', description: 'Captures whether submitters feel their concern was met.' },
      { label: 'Track response quality', description: 'Aggregates response and addressed rates across a priority list.' },
    ],
    glyphType: 'stakeholder-pulse',
    colorScheme: 'rose',
    runLabel: 'Open Stakeholder Pulse',
    onRun: () => { navigate('/prioritization/pulse'); },
  };
}

/**
 * Inversion Detector — prioritization inversions.
 * Relevant where rankings meet dependencies (Scoreboard, the Dependency
 * relationships page): it catches items ranked above something they hard-depend
 * on, which must be acknowledged before a priority list is published.
 */
export function inversionAgent(navigate: NavigateTo): AgentDescriptor {
  return {
    id: 'inversion',
    name: 'Inversion Detector',
    tagline: 'Catch priorities ranked above what they depend on',
    description:
      'A prioritization inversion is when an item is ranked above another item it hard-depends on — you have committed to finish something before the thing it needs. The detector finds these in a ranked set so they can be acknowledged, with rationale, before a priority list is published. It never re-ranks; your judgment is preserved.',
    capabilities: [
      { label: 'Scan the ranking for inversions', description: 'Compares rank order against hard dependency edges.' },
      { label: 'Surface what must be acknowledged', description: 'Lists each higher-ranked item that depends on a lower-ranked one.' },
      { label: 'Preserve the human rank', description: 'Records why an inversion is accepted instead of silently reordering.' },
    ],
    glyphType: 'inversion',
    colorScheme: 'amber',
    runLabel: 'Review in governance',
    onRun: () => { navigate('/prioritization/governance'); },
  };
}
